import { useMemo } from "react";

import { faEdit, faEye, faTable } from "@fortawesome/pro-regular-svg-icons";

import { TableBlockChangesModal } from "src/cases/caseView/history/events/TableBlockChangesModal";
import { resolveTableRecordDiff } from "src/cases/caseView/history/events/tableBlockDiff";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { useCase } from "src/cases/queries/cases";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";
import { getBlockIcon } from "src/cases/templates/caseBlocks/blockIcons";
import { IconButton } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const BlockDataEdited: React.FC<{
  entry: CaseHistoryEvent<"block_data_edited">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const { isOpen, isVisible, openModal, closeModal, afterLeave } = useModal();

  const { block_key, before_record, after_record } = entry.event_data;

  const blockConfig =
    caseData?.data.template_version?.case_blocks_configs?.[block_key];
  const tableConfig =
    blockConfig?.config.kind === "table" ? blockConfig.config : undefined;
  const blockTitle = blockConfig?.title ?? block_key;

  // Stable identity: the modal's row alignment is keyed off this, and the
  // history list re-renders on every poll.
  const diff = useMemo(
    () => resolveTableRecordDiff(before_record, after_record),
    [before_record, after_record],
  );

  return (
    <>
      <HistorySingleEntry
        actorId={entry.user_id}
        eventType={entry.event_type}
        headline={
          <>
            <Pill size="sm" variant="gray">
              <Pill.Icon icon={getBlockIcon(blockConfig?.icon) ?? faTable} />
              <Pill.Text>{blockTitle}</Pill.Text>
            </Pill>{" "}
            was edited.
          </>
        }
        icon={faEdit}
        isLast={isLast}
        sideAction={
          <Tooltip delay={250} placement="top-end" title="View changes" asChild>
            <IconButton
              dataLoc={`block-data-edited-view-changes-${block_key}`}
              icon={faEye}
              size="sm"
              onClick={openModal}
            />
          </Tooltip>
        }
        timestamp={entry.timestamp}
      />
      {isVisible && (
        <TableBlockChangesModal
          afterLeave={afterLeave}
          blockTitle={blockTitle}
          columnsConfig={tableConfig?.columns}
          defaultColumnsEditMode={tableConfig?.default_columns_edit_mode}
          diff={diff}
          editedAt={entry.timestamp}
          editedByUserId={entry.user_id}
          open={isOpen}
          onClose={closeModal}
        />
      )}
    </>
  );
};
